(() => {
  'use strict';

  const SH = window.SassyHive;
  if (!SH) return;

  const request = async (endpoint, payload, timeoutMs = 15000) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(`${SH.root}${endpoint}`.replace(/\/{2,}/g, '/'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: controller.signal,
        body: JSON.stringify(payload)
      });
      const text = await response.text();
      let result = {};
      try { result = text ? JSON.parse(text) : {}; } catch (_) {}
      if (!response.ok) throw new Error(result.description || result.message || `Shopify returned ${response.status}.`);
      return result;
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const readCartLines = () => {
    const node = document.querySelector('[data-sassy-hive-cart-lines]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Cart customization data could not be read.', error);
      return [];
    }
  };

  const pairId = (line) => String(
    line?.pairId ||
    line?.properties?.['_Customization ID'] ||
    line?.properties?.['_tsh_customization_id'] ||
    ''
  ).trim();

  const saveEdit = (lineKey) => {
    const lines = readCartLines();
    const box = lines.find((line) => line.key === lineKey && line.isMysteryBox);
    if (!box) {
      SH.toast('We could not reopen this customization. Refresh the cart and try again.');
      return;
    }
    const id = pairId(box);
    const video = lines.find((line) => line.isFeatureVideo && id && pairId(line) === id);
    const payload = {
      mysteryLineKey: box.key,
      mysteryVariantId: SH.normalizeVariantId(box.variantId),
      mysteryQuantity: 1,
      properties: box.properties || {},
      videoLineKey: video?.key || '',
      videoVariantId: SH.normalizeVariantId(video?.variantId),
      videoQuantity: 1,
      videoProperties: video?.properties || {},
      addons: lines.filter(line => ['42443754995767', '42443755028535'].includes(String(line.variantId)) && pairId(line) === id),
      customizationId: id || SH.createCustomizationId()
    };
    try {
      sessionStorage.setItem(SH.keys.edit, JSON.stringify(payload));
      window.location.assign(`${SH.root}#customize`);
    } catch (error) {
      console.error('Customization editing could not be started.', error);
      SH.toast('We could not reopen this customization. Please try again.');
    }
  };

  const currentBoxKey = (cart, id, fallback) => {
    const box = (cart.items || []).find((item) =>
      item.properties?.['_Customization ID'] === id &&
      Boolean(item.properties?.['Mystery Charms'] || item.properties?.['Mystery Capsules'])
    );
    return box?.key || fallback;
  };

  const rollbackBox = async (key, id, originalProperties) => {
    try {
      await request('cart/change.js', { id: key, quantity: 1, properties: originalProperties });
    } catch (_) {
      try {
        const response = await fetch(`${SH.root}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        await request('cart/change.js', {
          id: currentBoxKey(cart, id, key),
          quantity: 1,
          properties: originalProperties
        });
      } catch (error) {
        console.error('Customization rollback failed.', error);
      }
    }
  };

  const updateExisting = async (video, boxProperties, videoProperties, videoVariantId) => {
    const edit = SH.state.activeEdit;
    if (!edit?.mysteryLineKey) throw new Error('The cart item being edited could not be identified.');

    const original = { ...(edit.properties || {}) };
    const changedCart = await request('cart/change.js', {
      id: edit.mysteryLineKey,
      quantity: 1,
      properties: boxProperties
    });
    const newBoxKey = currentBoxKey(changedCart, edit.customizationId, edit.mysteryLineKey);

    try {
      if (video) {
        if (edit.videoLineKey) {
          await request('cart/change.js', {
            id: edit.videoLineKey,
            quantity: 1,
            properties: videoProperties
          });
        } else {
          await request('cart/add.js', {
            items: [{ id: videoVariantId, quantity: 1, properties: videoProperties }]
          });
        }
      } else if (edit.videoLineKey) {
        await request('cart/change.js', { id: edit.videoLineKey, quantity: 0 });
      }
    } catch (error) {
      await rollbackBox(newBoxKey, edit.customizationId, original);
      throw new Error(`${error.message} The original Mystery Box customization was restored.`);
    }
  };

  const setStatus = (message, type = 'info') => {
    const node = document.getElementById('cartAddStatus');
    if (!node) return;
    node.textContent = message || '';
    node.className = `cart-add-status ${message ? `is-${type}` : ''}`.trim();
    node.hidden = !message;
  };

  const setBusy = (busy) => {
    const button = document.getElementById('addConfiguredBoxButton');
    if (!button) return;
    if (busy) {
      button.dataset.originalText = button.textContent;
      button.textContent = SH.state.activeEdit ? 'Updating Your Box…' : 'Adding Your Box…';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      button.textContent = SH.state.activeEdit
        ? 'Update My Customization'
        : (button.dataset.originalText || 'Add My Customized Box to Cart');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  const refreshBadge = async () => {
    try {
      const response = await fetch(`${SH.root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const badge = document.getElementById('cartBadge');
      if (badge) badge.textContent = cart.item_count;
    } catch (_) {}
  };

  const addConfigured = async () => {
    if (!SH.quiz) return;
    setStatus('');
    const video = document.getElementById('videoYes')?.checked === true;
    let extras;
    try { extras = SH.quiz.extraItems(); } catch (error) { setStatus(error.message, 'error'); return; }

    if (video && document.getElementById('videoName')?.value === 'nickname' && !SH.quiz.value('videoNickname')) {
      setStatus('Please enter the nickname you want us to use in the Feature Video.', 'error');
      document.getElementById('videoNickname')?.focus();
      return;
    }
    if (video && !document.getElementById('videoConsent')?.checked) {
      setStatus('Please confirm the Feature Video permission before continuing.', 'error');
      document.getElementById('videoConsent')?.focus();
      return;
    }

    const tier = SH.state.selectedTier;
    const resolved = SH.resolveTier(tier.balls);
    const tierVariantId = SH.normalizeVariantId(
      SH.state.activeEdit?.mysteryVariantId || resolved?.variantId || tier.variantId
    );
    const videoVariantId = SH.normalizeVariantId(SH.config.video?.variantId);

    if (!tierVariantId) {
      setStatus(`The ${tier.balls}-charm tier is not connected to Shopify.`, 'error');
      return;
    }
    if (!SH.state.activeEdit && resolved?.available === false) {
      setStatus(`The ${tier.balls}-charm tier is currently unavailable.`, 'error');
      return;
    }
    if (video && !SH.state.activeEdit?.videoLineKey && !videoVariantId) {
      setStatus('The Feature Video product is not connected in the theme settings.', 'error');
      return;
    }
    if (video && !SH.state.activeEdit?.videoLineKey && SH.config.video?.available === false) {
      setStatus('The Feature Video add-on is currently unavailable.', 'error');
      return;
    }

    const id = SH.state.activeEdit?.customizationId || SH.createCustomizationId();
    const boxProperties = SH.quiz.buildBoxProperties(video, id);
    const videoProperties = SH.quiz.buildVideoProperties(id);

    setBusy(true);
    setStatus(SH.state.activeEdit ? 'Updating your saved customization…' : 'Adding your personalized Mystery Box…', 'loading');

    try {
      if (SH.state.activeEdit) {
        await updateExisting(video, boxProperties, videoProperties, videoVariantId);
        const existing = SH.state.activeEdit.addons || [];
        const additions = extras.filter(extra => !existing.some(line => String(line.variantId) === String(extra.variantId)));
        if (additions.length) await request('cart/add.js', { items: additions.map(extra => ({ id: extra.variantId, quantity: extra.quantity, properties: { '_Customization ID': id, 'Connected Box': `${tier.balls} Mystery Charms`, 'Recipient Name': boxProperties['Recipient Name'] } })) });
        const updates = {};
        const used = new Set();
        existing.forEach(line => {
          const extra = extras.find(item => String(item.variantId) === String(line.variantId));
          updates[line.key] = extra && !used.has(String(line.variantId)) ? extra.quantity : 0;
          used.add(String(line.variantId));
        });
        if (Object.keys(updates).length) await request('cart/update.js', { updates });
      } else {
        const items = [{ id: tierVariantId, quantity: 1, properties: boxProperties }];
        if (video) items.push({ id: videoVariantId, quantity: 1, properties: videoProperties });
        extras.forEach(extra => items.push({ id: extra.variantId, quantity: extra.quantity, properties: { '_Customization ID': id, 'Connected Box': `${tier.balls} Mystery Charms`, 'Recipient Name': boxProperties['Recipient Name'] } }));
        await request('cart/add.js', { items });
      }

      await refreshBadge();
      SH.quiz.clearDraft();
      try { sessionStorage.removeItem(SH.keys.edit); } catch (_) {}
      SH.state.activeEdit = null;
      setStatus('Your Mystery Box customization was saved successfully.', 'success');
      window.location.assign(SH.config.cartUrl || `${SH.root}cart`);
    } catch (error) {
      console.error('Unable to save the configured box.', error);
      const message = error?.name === 'AbortError'
        ? 'The cart took too long to respond. Your answers remain saved in this browser tab.'
        : (error?.message || `We could not save your customization. Email ${SH.config.supportEmail} for help.`);
      setStatus(message, 'error');
      SH.toast(message);
    } finally {
      setBusy(false);
    }
  };

  const restoreEdit = () => {
    if (!SH.quiz || !document.getElementById('quizForm')) return;
    try {
      const raw = sessionStorage.getItem(SH.keys.edit);
      if (!raw) return;
      const payload = JSON.parse(raw);
      if (!payload?.mysteryLineKey || !payload?.properties) return;
      SH.quiz.restoreFromProperties(payload);
    } catch (error) {
      console.warn('Saved customization edit could not be restored.', error);
    }
  };

  const initialize = () => {
    document.addEventListener('click', (event) => {
      const edit = event.target.closest('[data-edit-customization]');
      if (edit) {
        event.preventDefault();
        saveEdit(edit.dataset.lineKey);
      }
    });
    document.getElementById('addConfiguredBoxButton')?.addEventListener('click', addConfigured);
    restoreEdit();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
